<template>
  <table
    class="table"
    :class="`table--border-${borderStyle}`"
  >
    <caption
      v-if="caption"
      class="table__caption"
      v-text="caption"
    />

    <thead v-if="isFirstRowHeading">
      <tr>
        <th
          v-for="(tableHeading, index) of formattedTable[0]"
          :key="index"
          class="table__column-heading text-m-body-regular"
          v-text="tableHeading"
        />
      </tr>
    </thead>

    <tbody>
      <tr
        v-for="(tableRow, rowIndex) of tableBody"
        :key="rowIndex"
      >
        <template
          v-for="(column, columnIndex) of tableRow"
          :key="columnIndex"
        >
          <th
            v-if="columnIndex === 0 && column"
            class="table__row-heading"
            :class="getClasses(column)"
            v-text="column"
          />

          <td
            v-else-if="column"
            class="table__cell"
            :class="getClasses(column)"
            v-text="column"
          />
        </template>
      </tr>
    </tbody>
  </table>
</template>

<script>

/**
 * Vue: Table snippet (table-snippet)
 * -----------------------------------------------------------------------------
 * Snippet to create a table from a CSV.
 *
 * @param {String} [borderStyle] - Border style, accepts 'all', or 'row'.
 * @param {String} [caption] - Caption for table.
 * @param {Boolean} [isFirstRowHeading] - If the first row should be a heading.
 * @param {Object|Boolean} tableCsv - CSV for table.
 * @param {Boolean} [useAlternativeFont] - Use OCRB for numbers & small font for
 * text.
 * @param {Boolean} [useCenterText] - If table body text should be centered.
 *
 */

export default {
  name: 'TableSnippet',

  props: {
    borderStyle: {
      type: String,
      default: 'all',
      validator: (value) => {
        return value.split('-').every((part) => ['all', 'row'].includes(part))
      },
    },
    caption: {
      type: String,
      default: '',
    },
    isFirstRowHeading: {
      type: Boolean,
      default: false,
    },
    tableCsv: {
      type: String,
      default: '',
      required: true,
    },
    useAlternativeFont: {
      type: Boolean,
      default: false,
    },
    useCenterText: {
      type: Boolean,
      default: false,
    },
  },

  computed: {

    /**
     * Formats table from CSV data.
     * @returns {Array}
     */
    formattedTable() {
      const formattedTable = this.tableCsv.split('\n')

      return formattedTable.map((row) => {
        return row.split(',')
      })
    },

    /**
     * Return table body contents.
     * @returns {Array}
     */
    tableBody() {
      return this.isFirstRowHeading
        ? this.formattedTable.slice(1)
        : this.formattedTable
    },
  },

  methods: {

    /**
     * Return classes based on props and if cell contains numbers.
     * @param {String} column - text cell to render.
     * @returns {String}
     */
    getClasses(column) {
      if (!this.useAlternativeFont) {
        return 'text-body-m-regular'
      }

      const containsNumbers = (/\d/).test(column)

      const cellClasses = containsNumbers
        ? 'table__cell--padding table__cell--middle text-other-sale-price'
        : 'table__cell--middle text-body-s-regular'

      return this.useCenterText
        ? `${cellClasses} text-center`
        : cellClasses
    },
  },
}
</script>

<style lang="scss">
@import './table-snippet';
</style>
